import type { AxiosError, AxiosInstance } from 'axios'

import {
  HttpHeaders,
  HttpStatusCodes,
  MimeTypes,
  bearerAuthHeader,
} from '@climatepartner/unified-api-lib-http-utils'

import {
  RawError,
  UnifiedApiBadRequestError,
  UnifiedApiInternalServerError,
} from './types'

// SDK identification for tracking SDK usage vs direct API calls
export const SDK_IDENTIFIER = 'unified-api-sdk-v2'

export const setupAxiosInterceptors = (
  axiosInstance: AxiosInstance,
  getTokenFn?: () => Promise<string>,
): void => {
  let refreshPromise: Promise<string> | undefined

  axiosInstance.interceptors.request.use(async config => {
    // Add SDK identification header to all requests
    config.headers[HttpHeaders.X_SDK_VERSION] = SDK_IDENTIFIER

    if (getTokenFn) {
      const token = await getTokenFn()
      config.headers[HttpHeaders.AUTHORIZATION] = bearerAuthHeader(token)
    }
    return config
  })

  axiosInstance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      if (error.response?.status === HttpStatusCodes.UNAUTHORIZED) {
        const contentType = error.response.headers[
          HttpHeaders.CONTENT_TYPE
        ] as string
        if (contentType?.startsWith(MimeTypes.APPLICATION_JSON)) {
          try {
            const body = error.response.data as RawError
            if (body?.error?.code === 'JWT_EXPIRED') {
              // Refresh token and retry
              if (getTokenFn) {
                if (!refreshPromise) {
                  refreshPromise = getTokenFn()
                  refreshPromise.finally(() => {
                    refreshPromise = undefined
                  })
                }
                const newToken = await refreshPromise
                const config = error.config
                if (config) {
                  config.headers[HttpHeaders.AUTHORIZATION] =
                    bearerAuthHeader(newToken)
                  return axiosInstance(config)
                }
              }
            }
          } catch {
            // Continue to throw original error
          }
        }
      }

      if (
        error.response?.status &&
        error.response.status >= 400 &&
        error.response.status < 500
      ) {
        const rawError = error.response.data as RawError
        throw new UnifiedApiBadRequestError(
          error.response.status as HttpStatusCodes,
          rawError?.error?.code || 'UNKNOWN',
          rawError?.error?.message || 'Bad Request',
        )
      }

      if (error.response?.status && error.response.status >= 500) {
        const rawError = error.response.data as RawError
        throw new UnifiedApiInternalServerError(
          error.response.status as HttpStatusCodes,
          rawError?.error?.code || 'UNKNOWN',
          rawError?.error?.message || 'Internal Server Error',
        )
      }

      throw error
    },
  )
}
